import React from 'react';
import { addYears, format, isAfter, subDays } from 'date-fns';

import { DateInput, DatePicker } from '@zillow/constellation-10';

import type { Lease, Section } from '../../../../models/index';
import { LEASE_TERMS } from '../../../../constants';
import { DATE_PICKER_FORMAT } from '../../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  lease: Lease;
  hideLegalText: boolean;
  restrictToOneYear?: boolean;
};

export default function LeaseTermsEndDate({
  section,
  setSectionValues,
  hideLegalText,
  restrictToOneYear,
}: Props): JSX.Element {
  function onEndDateChange(formattedDate: string): void {
    setSectionValues({
      [LEASE_TERMS.FIELD_NAME.END_DATE]: formattedDate,
    });
    validateEndDate();
  }

  function validateEndDate(): void {
    const field = section.getField(LEASE_TERMS.FIELD_NAME.END_DATE);

    if (!hideLegalText) {
      const twoYearsAfterStart = addYears(getStartDate(), 2);
      const oneYearAfterStart = addYears(getStartDate(), 1);

      if (isAfter(getEndDate(), twoYearsAfterStart) && !restrictToOneYear) {
        field.setErrorMessage(LEASE_TERMS.ERROR_MESSAGE.END_DATE_PAST_TWO_YEARS);
      } else if (isAfter(getEndDate(), oneYearAfterStart) && restrictToOneYear) {
        field.setErrorMessage(LEASE_TERMS.ERROR_MESSAGE.END_DATE_PAST_ONE_YEAR);
      } else {
        field.setErrorMessage(LEASE_TERMS.ERROR_MESSAGE.END_DATE_IN_PAST);
      }
    } else {
      field.setErrorMessage(LEASE_TERMS.ERROR_MESSAGE.END_DATE_IN_PAST);
    }
  }

  function getStartDate(): string {
    return section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE);
  }

  function getEndDate(): string {
    return section.getFieldValue(LEASE_TERMS.FIELD_NAME.END_DATE);
  }

  return (
    <DatePicker
      dateFormat={DATE_PICKER_FORMAT}
      value={getEndDate()}
      onValueChange={(value) => {
        const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
        onEndDateChange(formatted);
      }}
      minDate={subDays(new Date(), 1)}
      closeModalOnDateSelect
      input={<DateInput />}
    />
  );
}
